import React, { useEffect, useState } from "react";
import * as styles from "./MobileNav.module.css";
import * as navStyles from "./NavSearch.module.css";
import Hamburger from "hamburger-react";
import { useForm } from "react-hook-form";
import {
  defaultSearchFormValues,
  handleSearchFormSubmit,
  handleSearchFormError,
} from "./DesktopNav";
import { NavigationItem } from "../../../types";
import { Cta } from "../../elements/Cta/Cta";
import { Link } from "../../elements/Link/Link";
import CloseButton from "../../elements/CloseButton/CloseButton";
import { brandColors } from "../../../lib/color";
import Arrow from "../../elements/Arrow/Arrow";
import { Equals } from "../../elements/Equals/equals";

interface Props {
  navigation: NavigationItem[];
  secondaryNavigation: NavigationItem[];
  setLoginOpen: Function;
}

export const MobileNav: React.FC<Props> = ({
  navigation,
  secondaryNavigation,
  setLoginOpen,
}) => {
  const [open, setOpen] = useState(false);
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({ defaultValues: defaultSearchFormValues });

  const [activePrimary, setActivePrimary] = useState<string | null | undefined>(
    ""
  );
  const [activePrimaryHeight, setActivePrimaryHeight] = useState(0);
  const [activeSecondary, setActiveSecondary] = useState<
    string | null | undefined
  >("");

  const handleClickLogin = (event: React.MouseEvent) => {
    setLoginOpen(true);
  };

  const handleCloseClick = (event: React.MouseEvent) => {
    setOpen(false);
  };

  const handlePrimaryButtonToggle = (event: React.MouseEvent<HTMLElement>) => {
    const { name } = event.currentTarget.dataset;

    if (activePrimary === name) {
      setActivePrimary("");
      setActivePrimaryHeight(0);
    } else {
      let newHeight = 0;
      const navContainer = event.currentTarget.parentElement?.querySelector(
        `[data-menu="${name}"]`
      ) as HTMLElement;

      if (navContainer) {
        const containerStyles = window.getComputedStyle(navContainer);
        newHeight =
          navContainer.offsetHeight +
          parseFloat(containerStyles["marginBottom"]);
      }

      setActivePrimary((event.currentTarget as HTMLElement)?.dataset.name);
      setActivePrimaryHeight(newHeight);
    }
  };

  const handleSecondaryButtonToggle = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    const { name } = event.currentTarget.dataset;
    setActiveSecondary(name);
  };

  const handleTertiaryBackClick = (event: React.MouseEvent<HTMLElement>) => {
    setActiveSecondary("");
  };

  useEffect(() => {
    function handleWindowClick(event: MouseEvent) {
      const clickedEl = event.target as HTMLElement;
      if (
        !clickedEl?.closest(".js-mobile-nav") &&
        open &&
        !clickedEl?.closest(".hamburger-react")
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("click", handleWindowClick, true);

    return function cleanup() {
      window.removeEventListener("click", handleWindowClick, true);
    };
  }, [open]);

  const renderPrimaryNavigation = () => {
    return navigation?.map((item, idxp) => {
      return (
        <li key={`primary${idxp}`}>
          {item.subnavigation && item.subnavigation.length ? (
            <button
              className={styles.mainNavItem}
              onClick={handlePrimaryButtonToggle}
              aria-expanded={item.title === activePrimary}
              data-name={item.title}
              aria-label={`show submenu for ${item.title}`}
            >
              {item.title}
              <Arrow
                className={styles.primaryArrow}
                direction={item.title === activePrimary ? "down" : "right"}
              />
            </button>
          ) : (
            <Link className={styles.mainNavItem} to={item.link.href}>
              {item.title}
            </Link>
          )}
          {item.subnavigation && renderPrimarySubnavigation(item)}
        </li>
      );
    });
  };

  const renderPrimarySubnavigation = (item: NavigationItem) => {
    return (
      <div
        className={styles.subNavContainer}
        style={{
          height: item.title === activePrimary ? activePrimaryHeight : 0,
        }}
      >
        <ul className={styles.subNavList} data-menu={item.title}>
          <li>
            <Link className={styles.secondaryNavItem} to={item.link.href}>
              {item.title} Overview
            </Link>
          </li>
          {item.subnavigation?.map((secondaryItem, idxs) => {
            return (
              <li
                key={`secondary${idxs}`}
                className={styles.secondaryNavItemContainer}
              >
                {secondaryItem.subnavigation ? (
                  renderSecondarySubnavigation(secondaryItem)
                ) : (
                  <Link
                    className={styles.secondaryNavItem}
                    to={secondaryItem.link.href}
                    aria-expanded={secondaryItem.title === activeSecondary}
                  >
                    {secondaryItem.title}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderSecondarySubnavigation = (secondaryItem: NavigationItem) => {
    return (
      <>
        <button
          className={styles.secondaryNavItem}
          onClick={handleSecondaryButtonToggle}
          aria-expanded={secondaryItem.title === activeSecondary}
          data-name={secondaryItem.title}
          aria-label={`show submenu for ${secondaryItem.title}`}
        >
          {secondaryItem.title}
          <Arrow className={styles.secondaryArrow} direction="right" />
        </button>
        <div className={styles.tertiaryNavContainer}>
          <ul className={styles.tertiaryNavList}>
            <li>
              <button
                className={styles.tertiaryBackBtn}
                onClick={handleTertiaryBackClick}
              >
                <Arrow
                  className={styles.tertiaryArrow}
                  direction="left"
                  color={brandColors.medgray}
                />
                <span className={styles.tertiaryBackLabel}>Menu</span>
                <br />
                {secondaryItem.title}
              </button>
            </li>
            <li>
              <Link to={secondaryItem.link.href}>
                {secondaryItem.title} Overview
              </Link>
            </li>
            {secondaryItem.subnavigation?.map((tertiaryItem, idxt) => {
              return (
                <li key={`tertiary${idxt}`}>
                  <Link to={tertiaryItem.link.href}>{tertiaryItem.title}</Link>
                </li>
              );
            })}
          </ul>
        </div>
      </>
    );
  };

  return (
    <div className={styles.navContainer}>
      <div className={styles.hamburgerContainer}>
        <Cta
          button
          buttonStyle="white"
          label="Login"
          onClick={handleClickLogin}
        />
        <Hamburger
          toggled={open}
          toggle={setOpen}
          label="Open Navigation Menu"
        />
      </div>
      <div
        className={`${styles.navWrapper} ${
          open ? styles.open : ""
        } js-mobile-nav`}
      >
        <nav className={styles.nav}>
          <div className={styles.closeNav}>
            <CloseButton
              color={brandColors.medgray}
              className={styles.closeNavBtn}
              onClick={handleCloseClick}
            />
          </div>
          <ul className={styles.mainNavList}>{renderPrimaryNavigation()}</ul>
        </nav>
        <form
          className={navStyles.searchContainer}
          onSubmit={handleSubmit(handleSearchFormSubmit, handleSearchFormError)}
        >
          <input type="text" {...register("searchText")} placeholder="SEARCH" />
          <button type="submit" aria-label="search">
            <img
              src="/images/search.svg"
              alt="Magnifying glass icon for search"
            />
          </button>
        </form>
        <nav className={styles.secondaryNav} aria-label="secondary">
          <ul className={styles.secondaryNavList}>
            {secondaryNavigation?.map((item, i) => {
              return (
                <li key={`secondaryNavMobile${i}`}>
                  <Link to={item.link.href}>{item.title}</Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className={styles.routing}>Routing Number: 101100579</div>

        <Equals className={styles.equals} id={`mobileNav`} />
      </div>
    </div>
  );
};
